// O MESMO voo pedido para várias pessoas (#4103). Uma order de viagem para o solicitante
// E um colega carrega um voucher por passageiro (o vínculo N→1 do trip-api é intencional),
// e cada voucher é um `TripService` — o documento daquele passageiro. O itinerário, porém,
// fala de JORNADAS: os dois bilhetes do mesmo voo são um passo só, senão a tela mostra o
// voo duas vezes, sem nome nenhum, e lê como compra em dobro.
import type { ServiceLeg, TripService } from './services.js';
import { isDeadVoucherStatus } from './status.js';

/** O voo como a companhia o opera, perna a perna — cia, número, rota e INSTANTES — mais o
 *  que torna a soma dos bilhetes honesta (fornecedor, moeda) e o lado vivo/morto: o
 *  cancelado não pode emprestar o card ao vivo, nem o vivo esconder o cancelado. Null —
 *  o bilhete fica sozinho — sem viajante identificado ou sem perna numerada e datada:
 *  aí nada prova que é o mesmo voo. Só VOO agrupa (é o que se pede para várias pessoas
 *  num pedido só); os outros modais seguem um passo por voucher. */
function journeySignature(service: TripService): string | null {
  if (service.detail?.kind !== 'FLIGHT' || !service.voucher.traveler?.id) return null;
  const legs: string[] = [];
  for (const leg of service.detail.legs) {
    if (!leg.number || !leg.departInstant) return null;
    legs.push(
      [
        leg.carrier,
        leg.number,
        leg.from,
        leg.to,
        leg.departInstant.toISOString(),
        leg.arriveInstant?.toISOString(),
      ].join('|'),
    );
  }
  if (legs.length === 0) return null;
  return [service.supplier, service.currency, isDeadVoucherStatus(service.status), ...legs].join(
    '§',
  );
}

/** Quão perto o bilhete está de valer. O passo do grupo veste o MENOS avançado: com um
 *  emitido e outro em emissão, "Emitido" mentiria sobre o segundo passageiro. A aprovação
 *  pendente vem antes do andamento (a mesma precedência do ícone da sidebar), e o emitido
 *  com cancelamento pedido antes do emitido limpo. O morto só se agrupa com morto. */
function progress(service: TripService): number {
  switch (service.status) {
    case null:
      return -1;
    case 'AWAITING_APPROVAL':
      return 0;
    case 'IN_PROGRESS':
      return 1;
    case 'CONFIRMED':
      return service.voucher.cancellationRequestedAt == null ? 3 : 2;
    case 'CANCELLED':
    case 'DECLINED':
    case 'EXPIRED':
    default:
      return 4;
  }
}

/** Uma jornada do itinerário: o `lead` (o bilhete menos avançado — o status do card) e
 *  `party`, o bilhete de cada passageiro, na ordem da viagem. */
export interface SameJourney {
  lead: TripService;
  party: TripService[];
}

/** Agrupa os serviços (já na régua do itinerário) por jornada. Viajantes DIFERENTES, e
 *  só: o mesmo viajante com dois bilhetes do mesmo voo é compra duplicada de verdade, e
 *  ela tem de continuar aparecendo duas vezes. */
export function sameJourneys(services: readonly TripService[]): SameJourney[] {
  // Uma jornada nunca é vazia: nasce com o serviço que a abriu.
  const parties: Array<[TripService, ...TripService[]]> = [];
  const open = new Map<string, [TripService, ...TripService[]]>();
  for (const service of services) {
    const signature = journeySignature(service);
    const party = signature == null ? undefined : open.get(signature);
    const traveler = service.voucher.traveler?.id;
    if (party && !party.some((mate) => mate.voucher.traveler?.id === traveler)) {
      party.push(service);
      continue;
    }
    const alone: [TripService, ...TripService[]] = [service];
    parties.push(alone);
    if (signature != null && !party) open.set(signature, alone);
  }
  return parties.map((party) => ({
    lead: party.reduce((least, s) => (progress(s) < progress(least) ? s : least), party[0]),
    party,
  }));
}

/** O valor quando TODOS o têm igual, senão null — a régua de tudo o que é de cada
 *  passageiro no passo do grupo. */
export function sharedValue<T>(values: readonly T[]): T | null {
  const [first] = values;
  const key = JSON.stringify(first);
  return values.every((value) => JSON.stringify(value) === key) ? (first ?? null) : null;
}

/** O que a perna diz de UM passageiro, não do voo. */
const PER_TRAVELER = [
  'seat',
  'baggage',
  'checkedBaggage',
  'baggageAllowance',
  'travelClass',
  'locator',
] as const satisfies readonly (keyof ServiceLeg)[];

/** A perna do passo do grupo: a do `lead`, com o que é de cada passageiro (assento,
 *  bagagem, classe, localizador) só quando é o mesmo para todos — o assento 14C do
 *  solicitante num card de "2 passageiros" seria do grupo inteiro aos olhos de quem lê. */
export function partyLeg(lead: ServiceLeg, mates: readonly (ServiceLeg | undefined)[]): ServiceLeg {
  if (mates.length < 2) return lead;
  const leg: ServiceLeg = { ...lead };
  for (const field of PER_TRAVELER) {
    Object.assign(leg, { [field]: sharedValue(mates.map((mate) => mate?.[field] ?? null)) });
  }
  return leg;
}
